import { Stack, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import {
  PanelItem,
  PanelItemDisplayTypeEnum,
  PanelItemFieldTypeEnum,
  PointOfInterest,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import { panelFeatureRegistry } from './panelFeatureRegistry';
import { usePoiImageName } from './usePoiImageName';
import { usePoiUpdateLocation } from './usePoiUpdateLocation';
import { formatPanelValue, JsonObject, JsonValue } from './panelValueFormatters';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { abyssColors } from '@/theme/colors';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { Pill } from '@/components/shared/Pill';
import { usePoiStatusLabels } from '@/hooks';
import * as state from '@/components/Analysis/state';
import { CustomTextField } from '../../../../../../shared/CustomField';

type Props = {
  item: PanelItem;
  poi: PointOfInterest;
};

function getNestedValue(source: JsonObject, key: string): JsonValue | undefined {
  return key.split('.').reduce<JsonValue | undefined>((current, segment) => {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object' || Array.isArray(current)) return undefined;
    return current[segment];
  }, source);
}

function getValueSource(
  fieldType: PanelItemFieldTypeEnum,
  poi: PointOfInterest
): JsonObject | undefined {
  const source = (() => {
    switch (fieldType) {
      case PanelItemFieldTypeEnum.TemplateField:
        return poi.templateFieldData;
      case PanelItemFieldTypeEnum.Blister:
        return poi.blister;
      case PanelItemFieldTypeEnum.PointOfInterest:
        return poi;
      default:
        return undefined;
    }
  })();
  return source ? (source as JsonObject) : undefined;
}

function resolveValue(item: PanelItem, poi: PointOfInterest, unitSystem: UnitSystemEnum): string {
  const source = getValueSource(item.fieldType, poi);
  if (!source) return NOT_APPLICABLE_LABEL;
  const raw = getNestedValue(source, item.key);
  return formatPanelValue({ fieldType: item.fieldType, key: item.key, raw, unitSystem });
}

function resolveLabel(item: PanelItem): string {
  return item.label ?? item.key;
}

const LOCATION_NAME_KEY = 'location.name';
const LOCATION_IN_3D_KEY = 'locationIn3d';

export const PanelItemRenderer = ({ item, poi }: Props) => {
  const { getPoiStatusLabel } = usePoiStatusLabels();
  const unitSystem = useAtomValue(state.unitSystem);
  const { imageName, goToImageView, valueTooltip: imageNameTooltip } = usePoiImageName(poi);
  const { handleUpdateLocation, poiHasLocation, isDisallowedType } = usePoiUpdateLocation(poi);

  if (item.feature) {
    const FeatureComponent = panelFeatureRegistry[item.feature];
    return FeatureComponent ? <FeatureComponent poi={poi} sectionKey={item.key} /> : null;
  }

  const label = resolveLabel(item);
  let value = resolveValue(item, poi, unitSystem);

  if (item.fieldType === PanelItemFieldTypeEnum.PointOfInterest && item.key === 'status') {
    value = getPoiStatusLabel(poi.status ?? NOT_APPLICABLE_LABEL, poi.type);
  }

  if (item.key === LOCATION_NAME_KEY) {
    value = imageName;
  }

  if (item.key === LOCATION_IN_3D_KEY) {
    value = poiHasLocation ? 'true' : 'false';
  }

  if (item.displayType === PanelItemDisplayTypeEnum.Pill) {
    return (
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="caption"
          fontWeight={500}
          whiteSpace="nowrap"
          color={abyssColors.primary[400]}
        >
          {label}
        </Typography>
        <Pill
          label={value ?? NOT_APPLICABLE_LABEL}
          size="small"
          state="default"
          status="linked"
          disabled
        />
      </Stack>
    );
  }

  if (item.displayType === PanelItemDisplayTypeEnum.User) {
    type UserShape = { name?: string; email?: string; picture?: string };
    let user: UserShape | null = null;

    if (item.fieldType === PanelItemFieldTypeEnum.PointOfInterest) {
      const source = getValueSource(item.fieldType, poi);
      const value = source ? source[item.key] : undefined;
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        user = value as UserShape;
      }
    }

    return (
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="caption"
          fontWeight={500}
          whiteSpace="nowrap"
          color={abyssColors.primary[400]}
        >
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" gap="0.4rem">
          {user && (
            <UserAvatar
              name={user.name ?? undefined}
              picture={user.picture ?? undefined}
              sx={{ width: '1.6rem', height: '1.6rem' }}
            />
          )}
          <Typography variant="h6" whiteSpace="nowrap" color={abyssColors.primary[500]}>
            {user?.name ?? user?.email ?? NOT_APPLICABLE_LABEL}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  const isLocationName = item.key === LOCATION_NAME_KEY;
  const isLocationIn3d = item.key === LOCATION_IN_3D_KEY && !isDisallowedType;

  // SIMPLE and LINK both use CustomTextField; LINK gets underline styling
  return (
    <CustomTextField
      mode="ReadOnly"
      id={label}
      title={label}
      value={value}
      underlined={
        item.displayType === PanelItemDisplayTypeEnum.Link && value !== NOT_APPLICABLE_LABEL
      }
      onClick={
        isLocationName
          ? (goToImageView as () => void)
          : isLocationIn3d
            ? (handleUpdateLocation as () => void)
            : undefined
      }
      valueTooltip={isLocationName ? imageNameTooltip : undefined}
      sx={{
        '& .read-only-title': {
          color: abyssColors.primary[400],
          variant: 'caption',
        },
        '& .read-only-value': {
          color: isLocationName ? abyssColors.primary[300] : abyssColors.primary[500],
          variant: 'h6',
        },
      }}
    />

    // <Stack direction="row" justifyContent="space-between" alignItems="center">
    //   <Typography
    //     variant="caption"
    //     fontWeight={500}
    //     whiteSpace="nowrap"
    //     color={abyssColors.primary[400]}
    //   >
    //     {label}
    //   </Typography>
    //   <Typography variant="h6" whiteSpace="nowrap" color={abyssColors.primary[500]}>
    //     {value}
    //   </Typography>
    // </Stack>
  );
};
